import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    FileText,
    CheckCircle,
    ShieldCheck,
    ClipboardList,
    BarChart3
} from 'lucide-react';
import ModuleLayout from '../../layouts/ModuleLayout';
import Drawer from '../../components/common/Drawer';
import PermissionGate from '../../components/common/PermissionGate/PermissionGate';

// Access Denied Component
const AccessDenied = () => (
    <div className="flex flex-col items-center justify-center p-12 text-center text-gray-500 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p>You do not have permission to view this section.</p>
    </div>
);

// Importing existing production components
import {
    PolicyLibrary,
    Acknowledgments,
    StatutoryCompliance
} from './compliance-policy';


const CompliancePolicyModule = ({ initialTab: propTab, initialAction: propAction, initialActionId: propId }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // STRICT TAB CONFIGURATION
    const COMPLIANCE_TABS = [
        { id: 'policyLibrary', label: 'Policy Library', icon: FileText },
        { id: 'acknowledgments', label: 'Policy Acknowledgements', icon: CheckCircle },
        { id: 'statutoryCompliance', label: 'Statutory Compliance', icon: ShieldCheck },
    ];

    // Map paths to tabs
    const getTabFromPath = useCallback((path) => {
        if (path.includes('/policy_library')) return 'policyLibrary';
        if (path.includes('/acknowledgments')) return 'acknowledgments';
        if (path.includes('/statutory_compliance')) return 'statutoryCompliance';
        return null;
    }, []);

    // Tab state managed via query param or prop
    const activeTab = searchParams.get('tab') || propTab || getTabFromPath(location.pathname) || 'policyLibrary';

    // Drawer state managed via query param or prop
    const action = searchParams.get('action') || propAction;
    const actionId = searchParams.get('id') || propId;

    const handleTabChange = (tabId) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);

            params.set('tab', tabId);
            params.delete('action');
            params.delete('id');

            const filterKeys = [
                'search',
                'status',
                'category',
                'policy',
                'department',
                'startDate',
                'endDate',
                'page',
                'limit',
                'sortBy',
                'sortOrder',
            ];
            filterKeys.forEach((key) => params.delete(key));

            return params;
        });
    };

    const closeDrawer = useCallback(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id');
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'policyLibrary': return <PolicyLibrary onBack={() => { }} />;
            case 'acknowledgments': return <Acknowledgments onBack={() => { }} />;
            case 'statutoryCompliance': return <StatutoryCompliance onBack={() => { }} />;
            default: return <PolicyLibrary onBack={() => { }} />;
        }
    };

    const getDrawerTitle = () => {
        switch (action) {
            case 'createPolicy': return 'Create Policy';
            case 'editPolicy': return 'Edit Policy';
            case 'viewPolicy': return 'Policy Details';
            case 'manageAcknowledgments': return 'Acknowledgment Tracking';
            case 'viewComplianceItem': return 'Compliance Details';
            default: return '';
        }
    };

    const renderDrawerContent = () => {
        if (!action) return null;
        switch (action) {
            case 'createPolicy': return <PolicyLibrary initialMode="create" onClose={closeDrawer} isDrawer />;
            case 'editPolicy': return <PolicyLibrary initialMode="edit" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'viewPolicy': return <PolicyLibrary initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'manageAcknowledgments': return <Acknowledgments initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'viewComplianceItem': return <StatutoryCompliance initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            default: return null;
        }
    };

    const isFullScreenDrawer = ['createPolicy', 'editPolicy', 'manageAcknowledgments'].includes(action);

    return (
        <PermissionGate permission="compliance_policy" action="read" showFallback fallback={<AccessDenied />}>
            <ModuleLayout
                title="Compliance & Policy"
                tabs={COMPLIANCE_TABS}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            >
                {renderTabContent()}
            </ModuleLayout>

            <Drawer
                isOpen={!!action}
                onClose={closeDrawer}
                title={getDrawerTitle()}
                size={isFullScreenDrawer ? 'full' : 'xl'}
            >
                {renderDrawerContent()}
            </Drawer>
        </PermissionGate>
    );
};

export default CompliancePolicyModule;
